import { useState } from 'react'
import cx from 'classnames'
import Styles from './AIRevision.module.scss'
import { useHover } from '@react-aria/interactions'
import { StreamContentData } from 'common/Prose/nodeViews/AISuggestion/StreamContentData'
import { PreviewMarkdown } from 'components/AIRevision/PreviewMarkdown'
import { Actions, Props as ActionProps } from 'components/AIRevision/Actions'
import { Revision } from 'types'
import { observer } from 'mobx-react-lite'
import { useAlmanacEditorState } from 'stores/EditorState'
import { clearRevisionMarks } from './clearRevisionMarks'
import { IconButton } from 'components/IconButton/IconButton'
import { Close } from 'almanac-ui/icons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useDiscardRevision } from 'hooks/queries/layers/revisions/useDiscardRevision'
import { useQueryClient } from 'react-query'
import { layersKeys } from 'hooks/queries/layers/layersKeys'

interface Props extends Omit<ActionProps, 'view'> {
  revision: Revision
  customPrompt: string
  onPromptChange: (prompt: string) => any
}

export const AIRevision = observer(({ revision, articleId, layerId, customPrompt, onPromptChange }: Props) => {
  const editorState = useAlmanacEditorState()
  const [isOverflown, setIsOverflown] = useState(false)
  const [streamContent, setStreamContent] = useState<string>('')
  const { isMobile } = useResponsiveDesign()
  const queryClient = useQueryClient()
  const { mutate: discardRevision } = useDiscardRevision(
    {
      articleKey: articleId,
      layerId: layerId,
      id: revision.id,
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(layersKeys.revision(revision.layer.id, revision.id))
      },
    },
  )

  const { hoverProps, isHovered } = useHover({})

  if (!editorState) {
    reportError('No editor state for revisions')
    return null
  }

  const showDivider = isOverflown && revision.completed

  const handleDiscard = () => {
    discardRevision()
    const tr = clearRevisionMarks(editorState.view.state.tr)
    editorState.view.dispatch(tr)
  }

  return (
    <>
      <div
        data-testid={`ai-revision-${revision.id}`}
        className={cx(Styles.content, { [Styles.withDivider]: showDivider })}
        ref={(node) => {
          if (node) {
            setIsOverflown(node.scrollHeight > node.clientHeight)
          }
        }}
        {...hoverProps}
      >
        {(isHovered && !isMobile) && (
          <IconButton
            aria-label="Discard"
            icon={Close}
            variant="tertiary"
            className={Styles.closeIcon}
            onPress={handleDiscard}
          />
        )}
        {revision.job_id && !revision.completed && !revision.failed && (
          <StreamContentData
            jobId={revision.job_id}
            view={editorState.view}
            streamContent={streamContent}
            setStreamContent={setStreamContent}
            className={Styles.contentPreview}
          />
        )}
        {revision.completed && revision.content && (
          <PreviewMarkdown markdown={revision.content} view={editorState.view} className={Styles.contentPreview} />
        )}
        {revision.failed && <p>Unfortunately we were unable to revise the content.</p>}
      </div>
      <Actions
        view={editorState.view}
        revision={revision}
        articleId={articleId}
        layerId={layerId}
        customPrompt={customPrompt}
        onPromptChange={onPromptChange}
      />
    </>
  )
})
